import { useEffect, useState } from "react"
import { Box, ButtonBase, Divider, LinearProgress, Popover, Stack, Typography } from "@mui/material"

import { timeApi } from "../api"
import { LoadingRows } from "../components/LoadingState"
import UserAvatar from "../components/UserAvatar"
import { decimalHours, humanDuration } from "./duration"
import { rise } from "../motion"

/**
 * Who logged these hours.
 *
 * ── Why a popover and not another dialog ────────────────────────────────────
 *
 * It is opened from inside things — a project dialog, a task row, the details
 * card — to answer one question about one number, and it should close the
 * moment the reader moves on. A dialog over a dialog is a stack somebody has to
 * climb back out of; this sits beside the figure that raised the question.
 *
 * ── Whose time it can show ──────────────────────────────────────────────────
 *
 * Exactly what the summary endpoint returns for the viewer. Without
 * `time_tracking:view_all` that is their own entries only, so the breakdown is
 * one row — and the heading says "Your time" rather than presenting it as the
 * whole team's, the same rule as every other figure.
 *
 * @param {object} props
 * @param {HTMLElement|null} props.anchorEl Null closes it.
 * @param {string} props.title What these hours are for: a project, a task.
 * @param {object} props.query Summary filters: project_id, or task_id (+ with_steps).
 */
export default function TimeBreakdown({ anchorEl, onClose, title, query }) {
  const [state, setState] = useState({ rows: null, scope: "own", error: null })
  const open = Boolean(anchorEl)
  const key = JSON.stringify(query || {})

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setState({ rows: null, scope: "own", error: null })
    timeApi
      .summary({ group_by: "user", ...JSON.parse(key) })
      .then((data) => {
        if (!cancelled) setState({ rows: data.groups || [], scope: data.scope || "own", error: null })
      })
      .catch((err) => {
        if (!cancelled) setState({ rows: [], scope: "own", error: err.userMessage || "Could not load the hours." })
      })
    return () => {
      cancelled = true
    }
  }, [open, key])

  const rows = state.rows || []
  const total = rows.reduce((sum, row) => sum + (Number(row.tracked_seconds) || 0), 0)

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { width: 340, maxWidth: "calc(100vw - 32px)", p: 2, borderRadius: 2.5 } } }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {state.scope === "all" ? "Hours by person" : "Your time"}
      </Typography>
      <Typography variant="body2" fontWeight={650} noWrap title={title}>
        {title}
      </Typography>

      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
        <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {state.rows ? humanDuration(total) || "0m" : "—"}
        </Typography>
        {state.rows && total > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {decimalHours(total)} h · {rows.length} {rows.length === 1 ? "person" : "people"}
          </Typography>
        ) : null}
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {state.rows === null ? (
        <LoadingRows rows={3} height={26} />
      ) : state.error ? (
        <Typography variant="body2" color="error">
          {state.error}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody has logged time here yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {rows.map((row, index) => {
            const seconds = Number(row.tracked_seconds) || 0
            const share = total > 0 ? Math.round((seconds / total) * 100) : 0
            return (
              <Box key={row.group_key ?? index} sx={rise(index)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <UserAvatar name={row.group_label || row.username} size={26} tooltip={false} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {row.group_label || row.username || "Someone"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.entry_count} {Number(row.entry_count) === 1 ? "entry" : "entries"}
                      {Number(row.billable_seconds) < seconds ? ` · ${humanDuration(row.billable_seconds) || "0m"} billable` : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body2" fontWeight={650} sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {humanDuration(seconds)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {share}%
                    </Typography>
                  </Box>
                </Stack>
                <LinearProgress variant="determinate" value={share} sx={{ mt: 0.75, height: 4, borderRadius: 2 }} />
              </Box>
            )
          })}
        </Stack>
      )}
    </Popover>
  )
}

/**
 * A logged-time figure that opens its breakdown.
 *
 * Shaped like text rather than a button, so a row of facts still reads as a
 * row of facts; the underline on hover and the pointer are what say it opens.
 */
export function TimeFigure({ seconds, onOpen, label, sx }) {
  if (!seconds) return null
  return (
    <ButtonBase
      onClick={(event) => {
        event.stopPropagation()
        onOpen(event.currentTarget)
      }}
      aria-label={label}
      title="Who logged these hours"
      sx={{
        font: "inherit",
        color: "inherit",
        borderRadius: 1,
        px: 0.5,
        mx: -0.5,
        fontVariantNumeric: "tabular-nums",
        textDecoration: "underline dotted",
        textUnderlineOffset: 3,
        textDecorationColor: "currentColor",
        "&:hover": { bgcolor: "action.hover", textDecorationStyle: "solid" },
        ...sx,
      }}
    >
      {humanDuration(seconds)}
    </ButtonBase>
  )
}
